import type { SystemStyleObject } from "@chakra-ui/react";

import type { AlertItem, CountryItem, CompanyItem } from "../api/types";

/** UI shape for AlertsTable */
export type AlertRow = {
  id: string;
  country: string;
  company: string;
  category: string;
  severity: string;
  delta: number;
  created: string;
  monthEndDate?: string | null;
  status: string;
  ticketId?: string | null;
  ticketNumber?: string | null;
  ticketSysId?: string | null;
  ticketCreatedAt?: string | null;
};

/** UI shape for CountryGrid / Leaderboard */
export type CountryRow = {
  country: string;
  code: string;
  score: number;
  missing: number;
  status: string;
  trend: "up" | "down" | "stable";
  source?: string;
  subregion?: string;
  year?: number | null;
  month?: string | null;
};

/** UI shape for CompanyTable */
export type CompanyDrilldownRow = {
  "Company Code": number | string;
  Domain: string;
  "From Date": string;
  "To Date": string;
  "Expected Days": number;
  "Actual Days": number;
  Delta: number;
  "Missing Dates List": string;
  Severity: string;
};

const TREND_MAP: Record<string, "up" | "down" | "stable"> = {
  Improving: "up",
  Declining: "down",
  Stable: "stable",
};

/** Read value from object supporting both camelCase and snake_case */
function getStr(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && typeof v === "string") return v;
  }
  return "";
}
function getNum(obj: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && typeof v === "number" && !Number.isNaN(v)) return v;
    if (v != null && typeof v === "string") {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
}

function getStrOrNumAsString(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && typeof v === "string") return v;
    if (v != null && typeof v === "number" && !Number.isNaN(v)) return String(v);
  }
  return "";
}

export function mapAlertItemToRow(item: AlertItem | Record<string, unknown>): AlertRow {
  const r = item as Record<string, unknown>;
  return {
    id: getStrOrNumAsString(r, "alertId", "alert_id", "id"),
    country: getStr(r, "country", "countryCode"),
    company: getStrOrNumAsString(r, "companycode", "companyCode", "company_code", "company"),
    category: getStr(r, "category"),
    severity: getStr(r, "severity") || "Moderate",
    delta: getNum(r, "deltaDays", "delta_days", "delta"),
    created: getStr(r, "firstSeenAt", "first_seen_at", "createdAt", "created_at", "created"),
    monthEndDate: getStr(r, "monthenddate", "monthEndDate") || null,
    status: getStr(r, "status") || "NEW",
    ticketId: (r["ticketId"] as string | null | undefined) ?? null,
    ticketNumber: (r["ticketNumber"] as string | null | undefined) ?? null,
    ticketSysId: (r["ticketSysId"] as string | null | undefined) ?? null,
    ticketCreatedAt: (r["ticketCreatedAt"] as string | null | undefined) ?? null,
  };
}

export function mapCountryItemToRow(item: CountryItem | Record<string, unknown>): CountryRow {
  const r = item as Record<string, unknown>;
  const healthStatus = (
    getStr(r, "healthStatus", "health_status", "status") || "moderate"
  ).toLowerCase();
  const trendStatus = getStr(r, "trendStatus", "trend_status") || "Stable";
  const name = getStr(r, "name");
  const country = getStr(r, "country", "code");
  return {
    country: name || country,
    code: country || (name ? name.slice(0, 2).toUpperCase() : ""),
    score: Math.round((getNum(r, "dqScore", "dq_score", "score") || 0) * 100) / 100,
    missing: getNum(r, "missingDays", "missing_days", "missing"),
    status: healthStatus,
    trend: TREND_MAP[trendStatus] ?? "stable",
    source: getStr(r, "source", "domain") || undefined,
    subregion: getStr(r, "subregion") || undefined,
    year: r.year != null ? getNum(r, "year") : null,
    month: getStr(r, "month") || undefined,
  };
}

function getCompanyCode(r: Record<string, unknown>): string | number {
  const v = r["Company Code"] ?? r.companyCode ?? r.company_code;
  if (v != null && typeof v === "number") return v;
  if (v != null && typeof v === "string") return v;
  return "";
}

export function mapCompanyItemToRow(
  item: CompanyItem | Record<string, unknown>
): CompanyDrilldownRow {
  const r = item as Record<string, unknown>;
  // Support both API shape (missingDatesList array) and display shape ("Missing Dates List" string)
  const rawMissing = r["Missing Dates List"] ?? r.missingDatesList ?? r.missing_dates_list;
  const missingList =
    Array.isArray(rawMissing) && rawMissing.length > 0
      ? rawMissing.map(String).join(", ")
      : typeof rawMissing === "string"
        ? rawMissing
        : "";
  return {
    "Company Code": getCompanyCode(r),
    Domain: getStr(r, "Domain", "domain"),
    "From Date": getStr(r, "From Date", "fromDate", "from_date"),
    "To Date": getStr(r, "To Date", "toDate", "to_date") || "-",
    "Expected Days": getNum(r, "Expected Days", "expectedDays", "expected_days"),
    "Actual Days": getNum(r, "Actual Days", "actualDays", "actual_days"),
    Delta: getNum(r, "Delta", "delta"),
    "Missing Dates List": missingList,
    Severity: getStr(r, "Severity", "severity") || "Moderate",
  };
}

/** Extract array from API response - handles data or data.data or data.countries etc. */
export function extractDataArray<T>(response: unknown, listKey?: string): T[] {
  if (response == null || typeof response !== "object") return [];
  const obj = response as Record<string, unknown>;
  const data = obj.data ?? obj;
  if (Array.isArray(data)) return data as T[];
  if (data != null && typeof data === "object") {
    const inner = data as Record<string, unknown>;
    const list = listKey
      ? inner[listKey]
      : (inner.data ?? inner.list ?? inner.items ?? inner.countries ?? inner.companies);
    if (Array.isArray(list)) return list as T[];
  }
  return [];
}

export function mapAlertsResponse(items: AlertItem[] | unknown): AlertRow[] {
  const list = Array.isArray(items) ? items : [];
  return list.map((item) => mapAlertItemToRow(item as AlertItem));
}

export function mapCountriesResponse(items: CountryItem[] | unknown): CountryRow[] {
  const list = Array.isArray(items) ? items : [];
  return list.map((item) => mapCountryItemToRow(item as CountryItem));
}

export function mapCompaniesResponse(items: CompanyItem[] | unknown): CompanyDrilldownRow[] {
  const list = Array.isArray(items) ? items : [];
  return list.map((item) => mapCompanyItemToRow(item as CompanyItem));
}

/**
 * Standard scrollbar styles for sidebar containers
 */
export const getScrollbarStyles = (): SystemStyleObject => ({
  "&::-webkit-scrollbar": {
    width: "8px",
    marginRight: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#97979b",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#686a6b",
  },
});
export const getTrend = (change?: string | number, suffix = "") => {
  if (change == null) {
    return { trend: "stable" as const, value: "No change" };
  }

  const num = Number(change);
  if (Number.isNaN(num)) {
    return { trend: "stable" as const, value: "No change" };
  }

  const trend = num > 0 ? "up" : num < 0 ? "down" : ("stable" as const);

  const formatted =
    suffix === "%"
      ? `${num > 0 ? "+" : ""}${num.toFixed(2)}% vs last month`
      : `${num > 0 ? "+" : ""}${num} vs last month`;

  return { trend, value: formatted };
};
/**
 * Extracts usable data from socket payload.
 * Handles wrapped ({ data: X }), args-style ({ 0: X }), and raw payload shapes.
 */
function extractPayload<T>(payload: unknown): T | null {
  if (payload == null) return null;
  if (typeof payload !== "object") return payload as T;
  const obj = payload as Record<string, unknown>;
  if (obj.data != null && typeof obj.data === "object") {
    return obj.data as T;
  }
  if (!Array.isArray(obj) && obj["0"] != null && typeof obj["0"] === "object") {
    return obj["0"] as T;
  }
  if (Array.isArray(obj) && obj.length > 0) {
    if (
      obj.length === 1 &&
      typeof obj[0] === "object" &&
      obj[0] !== null &&
      !Array.isArray(obj[0])
    ) {
      return obj[0] as T;
    }
    return payload as T;
  }
  return payload as T;
}

export function getSummaryData(socketPayload: unknown): Record<string, unknown> | null {
  const extracted = extractPayload<unknown>(socketPayload);
  if (extracted != null && typeof extracted === "object") {
    return extracted as Record<string, unknown>;
  }
  return null;
}

const MONTH_ORDER: Record<string, number> = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
};

type SocketTrendRow = {
  year: string;
  month: string;
  domain: string;
  dq_score?: number;
  dqScore?: number;
};
type ChartTrendRow = {
  month: string;
  year: string;
  overall: number;
  sales: number;
  purchase: number;
  stock: number;
};

const DOMAIN_COLORS: Record<string, string> = {
  Sales: "#3B82F6",
  Purchase: "#10B981",
  Stock: "#F59E0B",
};

function getYearMonthIndex(year: string, month: string): number {
  const y = parseInt(year, 10);
  const m = MONTH_ORDER[month] ?? 0;
  return y * 12 + m;
}

function getMonthLabel(_year: number, month1To12: number): string {
  const keys = Object.keys(MONTH_ORDER);
  return keys[month1To12 - 1] ?? "?";
}

/**
 * Transforms socket trends format into chart format.
 * Socket: [{ year, month, domain, dq_score }] (flat, one row per domain per month)
 * Chart: [{ month, year, overall, sales, purchase, stock }] (pivoted, one row per month)
 * Fills missing months with null values so lines connect.
 */
export function transformTrendsData(
  socketPayload: unknown,
  fallback: ChartTrendRow[]
): ChartTrendRow[] {
  let extracted = extractPayload<unknown>(socketPayload);

  if (Array.isArray(extracted) && extracted.length >= 2) {
    const first = extracted[0];
    const second = extracted[1];
    if (typeof first === "string" && Array.isArray(second) && second.length > 0) {
      extracted = second;
    }
  }

  if (!Array.isArray(extracted) || extracted.length === 0) {
    return fallback;
  }

  const rows = extracted as SocketTrendRow[];
  const domainMap: Record<string, "sales" | "purchase" | "stock"> = {
    Sales: "sales",
    Purchase: "purchase",
    Stock: "stock",
  };

  const byKey = new Map<string, { sales?: number; purchase?: number; stock?: number }>();
  for (const r of rows) {
    const key = `${r.year}-${r.month}`;
    const domain = domainMap[r.domain];
    if (!domain) continue;
    let row = byKey.get(key);
    if (!row) {
      row = {};
      byKey.set(key, row);
    }
    const score = r.dq_score ?? r.dqScore;
    if (score != null) row[domain] = Number(score);
  }

  if (byKey.size === 0) return fallback;

  const pivoted: ChartTrendRow[] = [];
  for (const [key, row] of byKey) {
    const [year, month] = key.split("-");
    const sales = row.sales ?? null;
    const purchase = row.purchase ?? null;
    const stock = row.stock ?? null;
    const values = [sales, purchase, stock].filter((v): v is number => v != null);
    const overall = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    pivoted.push({
      month,
      year,
      overall: Number(overall.toFixed(1)),
      sales: sales ?? 0,
      purchase: purchase ?? 0,
      stock: stock ?? 0,
    });
  }

  pivoted.sort((a, b) => getYearMonthIndex(a.year, a.month) - getYearMonthIndex(b.year, b.month));

  const minIdx = getYearMonthIndex(pivoted[0].year, pivoted[0].month);
  const maxIdx = getYearMonthIndex(
    pivoted[pivoted.length - 1].year,
    pivoted[pivoted.length - 1].month
  );

  const filled: ChartTrendRow[] = [];
  let prev: ChartTrendRow | null = null;
  for (let idx = minIdx; idx <= maxIdx; idx++) {
    const y = Math.floor(idx / 12);
    const m = idx % 12 || 12;
    const monthLabel = getMonthLabel(y, m);
    const existing = pivoted.find((p) => getYearMonthIndex(p.year, p.month) === idx);
    if (existing) {
      filled.push(existing);
      prev = existing;
    } else {
      filled.push({
        month: monthLabel,
        year: String(y),
        overall: prev?.overall ?? 0,
        sales: prev?.sales ?? 0,
        purchase: prev?.purchase ?? 0,
        stock: prev?.stock ?? 0,
      });
    }
  }

  return filled;
}

export type DomainChartRow = { name: string; value: number; color: string };

export function transformDomainsData(
  socketPayload: unknown,
  fallback: DomainChartRow[]
): DomainChartRow[] {
  let extracted = extractPayload<unknown>(socketPayload);

  if (Array.isArray(extracted) && extracted.length >= 2) {
    const first = extracted[0];
    const second = extracted[1];
    if (typeof first === "string" && Array.isArray(second) && second.length > 0) {
      extracted = second;
    }
  }

  if (!Array.isArray(extracted) || extracted.length === 0) return fallback;
  const rows = extracted as Record<string, unknown>[];
  const result: DomainChartRow[] = [];
  for (const r of rows) {
    const name = (r.name ?? r.domain) as string;
    const value = Number(r.value ?? r.completenessScore ?? r.score ?? r.dqScore ?? r.dq_score ?? 0);
    if (!name) continue;
    result.push({
      name,
      value,
      color: DOMAIN_COLORS[name] ?? fallback.find((f) => f.name === name)?.color ?? "#6B7280",
    });
  }
  return result.length > 0 ? result : fallback;
}

export type MissingByCountryRow = {
  country: string;
  missing: number;
  status: string;
};

export function transformMissingByCountryData(
  socketPayload: unknown,
  fallback: MissingByCountryRow[]
): MissingByCountryRow[] {
  const extracted = extractPayload<unknown>(socketPayload);
  if (!Array.isArray(extracted) || extracted.length === 0) return fallback;
  const rows = extracted as Record<string, unknown>[];
  const result: MissingByCountryRow[] = [];
  for (const r of rows) {
    const country = (r.country ?? r.countryCode ?? r.code) as string;
    const missing = Number(r.missing ?? r.missingDays ?? r.missing_days ?? 0);
    const status = (r.status ?? "moderate") as string;
    if (country == null) continue;
    result.push({ country, missing, status });
  }
  return result.length > 0 ? result.sort((a, b) => b.missing - a.missing) : fallback;
}

export type ValidationSummaryRow = {
  type: string;
  passed: number;
  failed: number;
  total: number;
};

const VALIDATION_FIELD_MAP: Array<{
  type: string;
  passedKey: string;
  totalKey?: string;
  failedKey?: string;
}> = [
  {
    type: "Temporal",
    passedKey: "temporalPassed",
    failedKey: "temporalFailed",
  },
  {
    type: "Duplicates",
    passedKey: "duplicatesPassed",
    failedKey: "duplicatesFailed",
  },
  {
    type: "Validity",
    passedKey: "validityPassed",
    failedKey: "validityFailed",
  },
  {
    type: "Referential",
    passedKey: "referentialPassed",
    failedKey: "referentialFailed",
  },
  {
    type: "Cross-Source",
    passedKey: "crossSourcePassed",
    failedKey: "crossSourceFailed",
  },
  { type: "Holiday", passedKey: "holidayPassed", failedKey: "holidayFailed" },
];

export function transformValidationSummaryData(
  socketPayload: unknown,
  fallback: ValidationSummaryRow[]
): ValidationSummaryRow[] {
  let extracted = extractPayload<unknown>(socketPayload);

  if (Array.isArray(extracted) && extracted.length >= 2) {
    const second = extracted[1];
    if (Array.isArray(second) && second.length > 0) extracted = second[0];
    else if (typeof second === "object" && second !== null) extracted = second;
  } else if (
    Array.isArray(extracted) &&
    extracted.length > 0 &&
    typeof extracted[0] === "object" &&
    extracted[0] !== null &&
    !Array.isArray(extracted[0])
  ) {
    extracted = extracted[0];
  }

  if (extracted == null || typeof extracted !== "object") return fallback;
  const obj = extracted as Record<string, unknown>;

  const rows: ValidationSummaryRow[] = [];

  for (const { type, passedKey, totalKey, failedKey } of VALIDATION_FIELD_MAP) {
    const passed = Number(obj[passedKey] ?? 0);
    const failedFromPayload = failedKey != null ? Number(obj[failedKey] ?? 0) : undefined;
    const total =
      failedFromPayload != null
        ? passed + failedFromPayload
        : Number(totalKey ? (obj[totalKey] ?? passed) : (obj.totalChecks ?? passed));
    const failed = failedFromPayload != null ? failedFromPayload : Math.max(0, total - passed);
    if (total > 0 || passed > 0) {
      rows.push({ type, passed, failed, total: total || passed });
    }
  }

  if (rows.length > 0) return rows;

  if (Array.isArray(extracted)) {
    const arr = extracted as Record<string, unknown>[];
    for (const r of arr) {
      const type = (r.type ?? r.validationType ?? r.category) as string;
      const passed = Number(r.passed ?? r.passedCount ?? r.passed_count ?? 0);
      const failed = Number(r.failed ?? r.failedCount ?? r.failed_count ?? 0);
      const total = Number(r.total ?? r.totalCount ?? r.total_count ?? passed + failed);
      if (type) rows.push({ type, passed, failed, total: total || passed + failed });
    }
  }

  return rows.length > 0 ? rows : fallback;
}

/** Leaderboard/countries Country shape used by Leaderboard and CountryGrid */
export type Country = {
  country: string;
  code: string;
  score: number;
  missing: number;
  status: string;
  trend: "up" | "down" | "stable";
  /** Source column = domain (e.g. "Sales", "Purchase", "Stock", "ALL") */
  source?: string;
  /** Subregion = region filter */
  subregion?: string;
  year?: number | null;
  month?: string | null;
};

/** Map full country name to 2-letter code for leaderboard when API only sends name */
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  Hungary: "HU",
  "Czech Republic": "CZ",
  "Bosnia and Herzegovina": "BA",
  Czechia: "CZ",
};

function countryNameToCode(name: string): string {
  return COUNTRY_NAME_TO_CODE[name] ?? name.slice(0, 2).toUpperCase();
}

/**
 * Transforms socket leaderboard format into Country[].
 * Socket: ["leaderboard", [{ country: "Hungary", scorePercentage: 96.1 }, ...]]
 * or raw array of { country, scorePercentage }
 */
export function transformLeaderboardData(socketPayload: unknown, fallback: Country[]): Country[] {
  let extracted = extractPayload<unknown>(socketPayload);
  if (Array.isArray(extracted) && extracted.length >= 2) {
    const first = extracted[0];
    const second = extracted[1];
    if (typeof first === "string" && Array.isArray(second)) {
      extracted = second;
    }
  }
  if (!Array.isArray(extracted) || extracted.length === 0) return fallback;
  const rows = extracted as Record<string, unknown>[];
  const result: Country[] = [];
  for (const r of rows) {
    const countryName = String(r.country ?? r.name ?? "");
    const scorePct = Number(r.scorePercentage ?? r.score ?? r.dqScore ?? r.dq_score ?? 0);
    if (!countryName) continue;
    const status = scorePct >= 95 ? "healthy" : scorePct >= 80 ? "moderate" : "critical";
    result.push({
      country: countryName,
      code: countryNameToCode(countryName),
      score: Math.round(scorePct * 10) / 10,
      missing: 0,
      status,
      trend: "stable",
    });
  }
  return result.length > 0 ? result.sort((a, b) => b.score - a.score) : fallback;
}

/**
 * Formats an ISO date string or date-like string to a normal readable format.
 * e.g. "2024-12-01T08:15:00Z" -> "1 Dec 2024, 8:15 AM"
 */
export function formatDateTime(value: string): string {
  if (value == null || String(value).trim() === "") return "—";
  const d = new Date(String(value).trim());
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
